/**
 * Nexora ports ↔ OpenIM WASM SDK 适配层（hexagonal gateway 实现）。
 *
 * 边界：UI/useImSession 只依赖 ports.ts 的 IImGateway + types.ts 的
 * 前端模型；本文件把 SDK 的回调式 API（CbEvents + login/REST 风 Promise）
 * 翻译成「状态机 + 监听器 + 纯净 Promise」三件套，并把 SDK 对象
 * （ConversationItem/MessageItem/...）map 成前端类型，SDK 类型不外泄。
 *
 * 三个适配要点：
 * 1. 事件→状态：OnConnectSuccess/Failed 驱动 idle→connecting→ready/error
 *    状态机；OnRecvNewMessages / OnConversationChanged / OnFriendApplication*
 *    扇出给注册的 listener 集合（多订阅者，返回退订函数）。
 * 2. 对象→模型：mapConversation/mapMessage/mapFriendApp 负责字段改名 +
 *    预览文本生成（contentType 101..105 → 文本/[图片]/...）。
 * 3. URL 归一化：OpenIM/MinIO 常回「http://127.0.0.1/object/...」（缺端口
 *    也缺 /openim-api 反代前缀）或带预签名 query 的地址，直接给 <img> 用
 *    会打 80 端口或过期；normalizePublicFaceUrl 统一改写成
 *    {apiAddr}/object/... 并剥掉 query/hash/.presigned。
 *
 * 静态资源：public/openim/*（openIM.wasm + sql-wasm.wasm）+
 * index.html 引入 wasm_exec.js；单例经 getImGateway() 获取。
 */
import {
    CbEvents,
    getSDK,
    type ConversationItem,
    type FriendApplicationItem,
    type MessageItem,
    type WSEvent,
    GroupJoinSource,
  } from "@openim/wasm-client-sdk";
  import type { IImGateway, ImConnectionState } from "../ports";
  import type {
    FriendApplication,
    ImBootstrap,
    ImConversation,
    ImMessage,
    SelfProfile,
    FriendProfile,
    JoinedGroupBrief,
    FriendBrief,
    GroupSearchHit,
  } from "../types";
  
  type OpenImSdk = ReturnType<typeof getSDK>;
  type Listener<T> = (v: T) => void;
  
  function previewOf(msg: MessageItem): string {
    if (msg.textElem?.content) return msg.textElem.content;
    const t = msg.contentType as unknown as number;
    if (t === 101) return String(msg.content || "");
    if (t === 102) return "[图片]";
    if (t === 103) return "[语音]";
    if (t === 104) return "[视频]";
    if (t === 105) return "[文件]";
    return "[消息]";
  }
  
  function mapConversation(
    c: ConversationItem,
    apiAddr = "",
  ): ImConversation {
    let latest = "";
    if (c.latestMsg) {
      try {
        latest = previewOf(JSON.parse(c.latestMsg) as MessageItem);
      } catch {
        latest = "";
      }
    }
    return {
      conversationID: c.conversationID,
      conversationType: c.conversationType as unknown as number,
      userID: c.userID || undefined,
      groupID: c.groupID || undefined,
      showName: c.showName || c.userID || c.groupID || c.conversationID,
      // 会话里的对方/群头像同样可能是裸 /object/ URL，需归一化才能给 Avatar 用
      faceURL: normalizePublicFaceUrl(c.faceURL || "", apiAddr),
      unreadCount: c.unreadCount || 0,
      latestMsgPreview: latest,
    };
  }
  
  function mapMessage(m: MessageItem,apiAddr = ""): ImMessage {
    const media = pickMediaRawUrl(m); // 
    return {
      clientMsgID: m.clientMsgID,
      sendID: m.sendID || "",
      recvID: m.recvID || "",
      groupID: m.groupID || "",
      senderNickname: m.senderNickname || m.sendID || "",
      contentType: m.contentType as unknown as number,
      content: previewOf(m),
      sendTime: m.sendTime || Date.now(),
      raw: m,
      mediaUrl: normalizePublicFaceUrl(media.mediaUrl,apiAddr) || undefined,
      snapshotUrl: normalizePublicFaceUrl(media.snapshotUrl, apiAddr) || undefined,
      fileName: media.fileName || undefined,
      fileSize: media.fileSize || undefined,
    };
  }
  
  function mapFriendApp(a: FriendApplicationItem): FriendApplication {
    return {
      fromUserID: a.fromUserID,
      fromNickname: a.fromNickname || a.fromUserID,
      toUserID: a.toUserID,
      toNickname: a.toNickname || a.toUserID,
      reqMsg: a.reqMsg || "",
      createTime: a.createTime || 0,
      handleResult: a.handleResult as unknown as number,
    };
  }
  
  /**
   * 把 OpenIM 返回的 faceURL 改成浏览器能打开的地址。
   *
   * publicRead 时常得到：http://127.0.0.1/object/{userID}/{file}
   * （无端口 → 打 80；也没有我们的 /openim-api 反代前缀）
   * 应改成：{apiAddr}/object/...  例 http://127.0.0.1:8080/openim-api/object/...
   * MinIO 预签名则去掉 query / .presigned，避免当长期 <img src>。
   */
  function normalizePublicFaceUrl(raw: string, apiAddr: string): string {
    let url = (raw || "").trim();
    if (!url) return "";
    try {
      const u = new URL(url);
      // 预签名 query / hash 不能留给 Avatar 长期用
      u.search = "";
      u.hash = "";
      // 有的实现把 .presigned 写在 path 末尾
      let path = u.pathname.replace(/\.presigned$/i, "");
      // OpenIM 对象 API：用 bootstrap 的反代基址重写，补端口 + /openim-api
      if (path.startsWith("/object/") && apiAddr) {
        const base = apiAddr.replace(/\/$/, "");
        return `${base}${path}`;
      }
      // 其它（如直连 MinIO :18005）：只清 query
      u.pathname = path;
      return u.toString().replace(/\.presigned$/i, "");
    } catch {
      // 非标准 URL：原样返回，交给上层报错
      return url;
    }
  }
  function readImageSize(file: File): Promise<{width: number; height: number}> {
    return new Promise((resolve,reject)=>{
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = ()=>{
        const width = img.naturalWidth || 1;
        const height = img.naturalHeight || 1;
        URL.revokeObjectURL(url);
        resolve({width,height});
      };
      img.onerror = ()=>{
        URL.revokeObjectURL(url);
        reject(new Error("读取图片尺寸失败"));
      };
      img.src = url;
    });
  }
  /**
   * 从视频首帧截取一张当作封面
   * openim要求必须有snapshot
   */
  export function captureVideoSnapshot(file: File): Promise<{
    snapshotFile: File;
    durationSec: number;
    snapshotWidth: number;
    snapshotHeight: number;
  }> {
    return new Promise((resolve,reject)=>{
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      // preload=metadata：只拉元数据/首帧，不要写成 video.src = "metadata"
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = url;

      const fail = (msg: string)=>{
        URL.revokeObjectURL(url);
        reject(new Error(msg));
      };
      video.onloadeddata = () => {
        // 跳到 0.1s，避免部分容器首帧全黑
        try {
          video.currentTime = Math.min(0.1, (video.duration || 1) * 0.01);
        } catch {
          /* 忽略 seek 失败，直接画当前帧 */
        }
      };
      video.onseeked = ()=>{
        try {
          const w = video.videoWidth || 320;
          const h = video.videoHeight || 240;
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h; 
          const ctx = canvas.getContext("2d");
          if (!ctx) return fail("获取 canvas 上下文失败");
          ctx.drawImage(video,0,0,w,h); // 画当前帧
          canvas.toBlob((blob)=>{
            URL.revokeObjectURL(url); // 释放 url
            if (!blob) return fail("截取封面失败");
            const snapshotFile = new File([blob],"snapshot.jpg",{type:"image/jpeg"});
            resolve({ // 成功返回
              /**
               * 封面文件：类型为 jpeg，分辨率与视频一致
               */
              snapshotFile,
              // OpenIM 要「秒」；HTMLVideoElement.duration 已是秒
              durationSec: Math.max(1, Math.round(video.duration || 1)),
              snapshotWidth: w,
              snapshotHeight: h,
            });
          },"image/jpeg",0.85,);
        }catch (e) {
          fail(e instanceof Error ? e.message : String(e));
        }
      };
      video.onerror = ()=>fail("无法加载视频元数据");
    })
  }
  // 从sdk messageItem里尽量抽出可展示url
  /** 从 SDK MessageItem 里尽量抽出可展示 URL（未归一化） */
function pickMediaRawUrl(m: MessageItem): {
  mediaUrl: string;
  snapshotUrl: string;
  fileName: string;
  fileSize: number;
} {
  const pic = m.pictureElem as
    | {
        sourcePicture?: { url?: string };
        bigPicture?: { url?: string };
        snapshotPicture?: { url?: string };
      }
    | undefined;
  const file = m.fileElem as
    | { sourceUrl?: string; fileName?: string; fileSize?: number }
    | undefined;
  const video = m.videoElem as
    | { videoUrl?: string; snapshotUrl?: string }
    | undefined;
  const mediaUrl =
    pic?.bigPicture?.url ||
    pic?.sourcePicture?.url ||
    pic?.snapshotPicture?.url ||
    video?.videoUrl ||
    file?.sourceUrl ||
    "";
  const snapshotUrl = pic?.snapshotPicture?.url || video?.snapshotUrl || "";
  return {
    mediaUrl: String(mediaUrl || ""),
    snapshotUrl: String(snapshotUrl || ""),
    fileName: String(file?.fileName || ""),
    fileSize: Number(file?.fileSize || 0),
  };
}

  export class OpenImSdkGateway implements IImGateway {
    private sdk: OpenImSdk | null = null;
    private state: ImConnectionState = "idle";
    private apiAddr: string = "";
    private stateListeners = new Set<
      Listener<{ s: ImConnectionState; err?: string }>
    >();
    private convListeners = new Set<Listener<ImConversation[]>>();
    private msgListeners = new Set<Listener<ImMessage[]>>();
    private friendAppListeners = new Set<Listener<void>>();
  
    private setState(s: ImConnectionState, err?: string) {
      this.state = s;
      this.stateListeners.forEach((cb) => cb({ s, err }));
    }
  
    private notifyFriendApps() {
      this.friendAppListeners.forEach((cb) => cb());
    }
  
    async connect(boot: ImBootstrap): Promise<void> {
      this.setState("connecting");
      this.sdk = getSDK({
        coreWasmPath: "/openim/openIM.wasm",
        sqlWasmPath: "/openim/sql-wasm.wasm",
      });
  
      this.sdk.on(CbEvents.OnRecvNewMessages, (ev: WSEvent<MessageItem[]>) => {
        this.msgListeners.forEach((cb) =>
          cb((ev.data || []).map((m) => mapMessage(m, this.apiAddr))),
        );
      });
      this.sdk.on(CbEvents.OnConversationChanged, () => {
        void this.getConversations()
          .then((list) => this.convListeners.forEach((cb) => cb(list)))
          .catch(() => undefined);
      });
      // 好友申请：新增 / 同意 / 拒绝 都刷新收件箱
      this.sdk.on(CbEvents.OnFriendApplicationAdded, () => this.notifyFriendApps());
      this.sdk.on(CbEvents.OnFriendApplicationAccepted, () =>
        this.notifyFriendApps(),
      );
      this.sdk.on(CbEvents.OnFriendApplicationRejected, () =>
        this.notifyFriendApps(),
      );
      this.sdk.on(CbEvents.OnConnectFailed, (ev: WSEvent) => {
        this.setState("error", ev.errMsg || "OpenIM 连接失败");
      });
      this.sdk.on(CbEvents.OnConnectSuccess, () => this.setState("ready"));
      
      // 其他设备改了资料时,刷新本地展示
      this.sdk.on(CbEvents.OnSelfInfoUpdated,()=>{
        this.getSelfProfile();
      })
      try {
        await this.sdk.login({
          userID: boot.im_user_id,
          token: boot.im_token,
          platformID: boot.platform_id,
          apiAddr: boot.api_addr,
          wsAddr: boot.ws_addr,
        });
        // 缓存 apiAddr 去掉末尾 /
        this.apiAddr = (boot.api_addr || "").replace(/\/$/,"");
        this.setState("ready");
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        this.setState("error", msg);
        throw e instanceof Error ? e : new Error(msg);
      }
    }
  
    async disconnect(): Promise<void> {
      try {
        await this.sdk?.logout();
      } finally {
        this.sdk = null;
        this.setState("idle");
      }
    }
  
    private requireSdk(): OpenImSdk {
      if (!this.sdk) throw new Error("OpenIM 未连接");
      return this.sdk;
    }
  
    async getConversations(): Promise<ImConversation[]> {
      const { data, errCode, errMsg } =
        await this.requireSdk().getAllConversationList();
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      // 传入 apiAddr，把会话 faceURL 里的 /object/ 改成可经反代加载的地址
      return (data || []).map((c) => mapConversation(c, this.apiAddr));
    }
  
    async getHistory(conversationID: string, count = 40): Promise<ImMessage[]> {
      const { data, errCode, errMsg } =
        await this.requireSdk().getAdvancedHistoryMessageList({
          conversationID,
          count,
          startClientMsgID: "",
          viewType: 0,
        });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return (data?.messageList || []).map((m) => mapMessage(m, this.apiAddr));
    }
  
    /**
     * 标记会话已读：清 unreadCount；单聊还会发已读回执。
     * OpenIM WASM API：markConversationMessageAsRead(conversationID: string)
     */
    async markConversationAsRead(conversationID: string): Promise<void> {
      const id = (conversationID || "").trim();
      if (!id) return;
      const { errCode, errMsg } =
        await this.requireSdk().markConversationMessageAsRead(id);
      if (errCode !== 0) {
        throw new Error(
          errMsg || `markConversationMessageAsRead failed: ${errCode}`,
        );
      }
    }
    /** 查询当前用户资料（faceURL 会归一化，旧的坏 /object/ 地址也能显示） */
    async getSelfProfile(): Promise<SelfProfile> {
      // 确保已经登录，否则 sdk 会报未连接
      const { data, errCode, errMsg } = await this.requireSdk().getSelfUserInfo();
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return {
        userID: String(data?.userID || ""),
        nickname: String(data?.nickname || ""),
        // 读出来就改写，避免「库里已是 http://127.0.0.1/object/...」时 Avatar 打 80 端口
        faceURL: normalizePublicFaceUrl(String(data?.faceURL || ""), this.apiAddr),
      };
    }
    /**更新资料,只传要改的字段 */
    async updateSelfProfile(patch:{
      nickname?: string;
      faceURL?: string;
    }): Promise<void> {
      const body: {nickname?: string; faceURL?: string} = {};
      if (patch.nickname !== undefined) body.nickname = patch.nickname.trim();
      if (patch.faceURL !== undefined) body.faceURL = patch.faceURL.trim();
      // 一个字段都没有就没必要调用sdk了
      if (!body.nickname && !body.faceURL) throw new Error("没有可更新的资料字段");
      const { errCode, errMsg } = await this.requireSdk().setSelfInfo(body); 
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    
    }
    /**
     * 上传头像文件，返回 data.url。
     * SDK：uploadFile({ name, contentType, uuid, file })
     * 注意：仅上传成功 ≠ 资料已改；还要再调 updateSelfProfile。
     */
    async uploadAvatar(file: File): Promise<string> {
      // 简单校验：必须是图片
      if (!file.type.startsWith("image/")) {
        throw new Error("请选择图片文件");
      }
      // 限制大小，例如 5MB，避免把 MinIO/带宽打爆
      const maxBytes = 5 * 1024 * 1024;
      if (file.size > maxBytes) {
        throw new Error("头像不能超过 5MB");
      }
      // 每次上传一个稳定任务 ID，便于和进度事件关联（本例不听进度）
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      // 调 WASM 上传
      const { data, errCode, errMsg } = await this.requireSdk().uploadFile({
        // 原始文件名
        name: file.name || "avatar.png",
        // MIME，缺省用通用二进制
        contentType: file.type || "application/octet-stream",
        // 任务 ID
        uuid,
        // 浏览器 File 对象
        file,
      });
      // 上传失败
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      // 取出远程 URL
      const raw = String(data?.url || "").trim();
      // URL 为空说明存储配置异常（常见是 MinIO 未通）
      if (!raw) throw new Error("上传成功但未返回 url，请检查 OpenIM MinIO");
      // 关键：把 http://127.0.0.1/object/... 改成
      //       http://127.0.0.1:8080/openim-api/object/...
      // 再交给上层 setSelfInfo；否则 Avatar 会打到 80 端口
      const url = normalizePublicFaceUrl(raw, this.apiAddr);
      if (!url) throw new Error("faceURL 归一化后为空");
      return url;
    }
    async sendText(params: {
      text: string;
      recvID: string;
      groupID?: string;
      isGroup: boolean;
    }): Promise<ImMessage> {
      const sdk = this.requireSdk();
      const created = await sdk.createTextMessage(params.text);
      if (created.errCode !== 0) {
        throw new Error(created.errMsg || "createTextMessage failed");
      }
      const sent = await sdk.sendMessage({
        recvID: params.isGroup ? "" : params.recvID,
        groupID: params.isGroup ? params.groupID || params.recvID : "",
        message: created.data,
      });
      if (sent.errCode !== 0) {
        throw new Error(sent.errMsg || "sendMessage failed");
      }
      return mapMessage(sent.data || created.data, this.apiAddr);
    }

    /** 统一：把 create* 得到的 message 发出去，并映射为 ImMessage */
    private async sendCreated(
      message: MessageItem,
      params: { recvID: string; groupID?: string; isGroup: boolean },
    ): Promise<ImMessage> {
      const sent = await this.requireSdk().sendMessage({
        recvID: params.isGroup ? "" : params.recvID,
        groupID: params.isGroup ? params.groupID || params.recvID : "",
        message,
      });
      if (sent.errCode !== 0) {
        throw new Error(sent.errMsg || "sendMessage failed");
      }
      return mapMessage(sent.data || message, this.apiAddr);
    }

    async sendImage(params: {
      file: File;
      recvID: string;
      groupID?: string;
      isGroup: boolean;
    }): Promise<ImMessage> {
      if (!params.file.type.startsWith("image/")) {
        throw new Error("请选择图片文件");
      }
      if (params.file.size > 20 * 1024 * 1024) {
        throw new Error("图片不能超过 20MB");
      }
      const { width, height } = await readImageSize(params.file);
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}`;
      // url 留空：sendMessage 时由 SDK 上传到对象存储
      const picture = {
        uuid,
        type: params.file.type || "image/png",
        size: params.file.size,
        width,
        height,
        url: "",
      };
      const created = await this.requireSdk().createImageMessageByFile({
        file: params.file,
        sourcePicture: picture,
        bigPicture: picture,
        snapshotPicture: picture,
        sourcePath: params.file.name || "image.png",
      });
      if (created.errCode !== 0) {
        throw new Error(created.errMsg || "createImageMessageByFile failed");
      }
      return this.sendCreated(created.data, params);
    }

    async sendFile(params: {
      file: File;
      recvID: string;
      groupID?: string;
      isGroup: boolean;
    }): Promise<ImMessage> {
      if (params.file.size > 50 * 1024 * 1024) {
        throw new Error("文件不能超过 50MB");
      }
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}`;
      const created = await this.requireSdk().createFileMessageByFile({
        file: params.file,
        filePath: params.file.name,
        fileName: params.file.name,
        uuid,
        sourceUrl: "",
        fileSize: params.file.size,
        fileType: params.file.type || "application/octet-stream",
      });
      if (created.errCode !== 0) {
        throw new Error(created.errMsg || "createFileMessageByFile failed");
      }
      return this.sendCreated(created.data, params);
    }

    async sendVideo(params: {
      videoFile: File;
      snapshotFile: File;
      durationSec: number;
      snapshotWidth: number;
      snapshotHeight: number;
      recvID: string;
      groupID?: string;
      isGroup: boolean;
    }): Promise<ImMessage> {
      if (!params.videoFile.type.startsWith("video/")) {
        throw new Error("请选择视频文件");
      }
      if (params.videoFile.size > 100 * 1024 * 1024) {
        throw new Error("视频不能超过 100MB");
      }
      const created = await this.requireSdk().createVideoMessageByFile({
        videoFile: params.videoFile,
        snapshotFile: params.snapshotFile,
        videoPath: params.videoFile.name,
        duration: params.durationSec,
        videoType: params.videoFile.type || "video/mp4",
        videoUUID: crypto.randomUUID(),
        videoUrl: "",
        videoSize: params.videoFile.size,
        snapshotPath: params.snapshotFile.name,
        snapshotUUID: crypto.randomUUID(),
        snapshotSize: params.snapshotFile.size,
        snapshotUrl: "",
        snapshotWidth: params.snapshotWidth,
        snapshotHeight: params.snapshotHeight,
        snapShotType: params.snapshotFile.type || "image/jpeg",
      });
      if (created.errCode !== 0) {
        throw new Error(created.errMsg || "createVideoMessageByFile failed");
      }
      return this.sendCreated(created.data, params);
    }
  
    async addFriend(userID: string, reqMsg = "你好，加个好友"): Promise<void> {
      const { errCode, errMsg } = await this.requireSdk().addFriend({
        toUserID: userID,
        reqMsg,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }
  
    async getFriendApplicationsAsRecipient(): Promise<FriendApplication[]> {
      const { data, errCode, errMsg } =
        await this.requireSdk().getFriendApplicationListAsRecipient();
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return (data || []).map(mapFriendApp);
    }
  
    async acceptFriendApplication(
      fromUserID: string,
      handleMsg = "已同意",
    ): Promise<void> {
      // SDK：toUserID = 申请人 userID
      const { errCode, errMsg } = await this.requireSdk().acceptFriendApplication({
        toUserID: fromUserID,
        handleMsg,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }
  
    async refuseFriendApplication(
      fromUserID: string,
      handleMsg = "已拒绝",
    ): Promise<void> {
      const { errCode, errMsg } = await this.requireSdk().refuseFriendApplication({
        toUserID: fromUserID,
        handleMsg,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }
    // 建群
    async createGroup(params: {
      groupName: string;
      memberUserIDs: string[];
    }): Promise<string> {
      const { data, errCode, errMsg } = await this.requireSdk().createGroup({
        groupInfo: { groupName: params.groupName },
        memberUserIDs: params.memberUserIDs,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return data?.groupID || "";
    }


    /**
     * 拉好友列表 -> FriendBrief[]
     * faceURL 做 /object/ 归一化,和会话头像同一套
     */
    /**
     * 拉好友列表 → FriendBrief[]（建群多选用）。
     * filterBlack=true：不把已拉黑的人放进选人列表。
     * faceURL 做 /object/ 归一化，和会话头像同一套。
     * 注意：只走 OpenIM SDK，不打 Nexora /api/im。
     */
    async listFriends(): Promise<FriendBrief[]> {
      const { data, errCode, errMsg } =
        await this.requireSdk().getFriendList(true);
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return (data || []).map((f) => {
        const nickname = f.nickname || f.userID;
        const remark = f.remark || "";
        return {
          userID: f.userID,
          nickname,
          remark,
          // 备注优先，和常见 IM 展示习惯一致
          displayName: (remark || nickname || f.userID).trim(),
          faceURL: normalizePublicFaceUrl(f.faceURL || "", this.apiAddr),
        };
      });
    }

    /**
     * 搜群：同时搜群名 + 群 ID（OpenIM SearchGroupsParams）。
     * 搜不到结果≠接口坏了，可能是群未开放被搜。
     */
    async searchGroups(keyword: string): Promise<GroupSearchHit[]> {
      const q = (keyword || "").trim();
      if (!q) return [];
      const { data, errCode, errMsg } = await this.requireSdk().searchGroups({
        keywordList: [q],
        isSearchGroupID: true,
        isSearchGroupName: true,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return (data || []).map((g) => ({
        groupID: g.groupID || "",
        groupName: g.groupName || g.groupID || "",
        faceURL: normalizePublicFaceUrl(g.faceURL || "", this.apiAddr),
        memberCount: g.memberCount || 0,
      }));
    }

    /**
     * 申请加群（SDK joinGroup）。
     * joinSource=Search：表示从搜索入口加入。
     * 群若开启入群验证，只会提交申请，不会立刻出现在会话列表。
     */
    async joinGroup(params: {
      groupID: string;
      reqMsg?: string;
    }): Promise<void> {
      const groupID = (params.groupID || "").trim();
      if (!groupID) throw new Error("groupID 为空");
      const { errCode, errMsg } = await this.requireSdk().joinGroup({
        groupID,
        reqMsg: (params.reqMsg || "申请加入群聊").trim(),
        joinSource: GroupJoinSource.Search,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }

    /**
     * 退出群聊。
     * 群主需先转让或走 dismissGroup；本方法只封装 quitGroup。
     */
    async quitGroup(groupID: string): Promise<void> {
      const id = (groupID || "").trim();
      if (!id) throw new Error("groupID 为空");
      const { errCode, errMsg } = await this.requireSdk().quitGroup(id);
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }

    /**
 * 好友资料：先 getSpecifiedFriendsInfo，没有再 getUsersInfo。
 * faceURL 同样做 /object/ 归一化，Avatar 才能显示。
 */
    async getFriendProfile(userID: string): Promise<FriendProfile> {
      const id = (userID || "").trim();
      if (!id) throw new Error("userID 为空");
      const sdk = this.requireSdk();

      // 1) 尝试当好友查（带 remark）
      const friendRes = await sdk.getSpecifiedFriendsInfo({
        friendUserIDList: [id],
        filterBlack: false,
      });
      if (friendRes.errCode === 0 && friendRes.data?.[0]) {
        const f = friendRes.data[0];
        return {
          userID: f.userID,
          nickname: f.nickname || f.userID,
          faceURL: normalizePublicFaceUrl(f.faceURL || "", this.apiAddr),
          remark: f.remark || "",
          isFriend: true,
        };
      }

      // 2) 非好友：公开资料
      const userRes = await sdk.getUsersInfo([id]);
      if (userRes.errCode !== 0) {
        throw new Error(userRes.errMsg || "getUsersInfo failed");
      }
      const u = userRes.data?.[0];
      return {
        userID: id,
        nickname: u?.nickname || id,
        faceURL: normalizePublicFaceUrl(u?.faceURL || "", this.apiAddr),
        remark: "",
        isFriend: false,
      };
    }

    async deleteFriend(userID: string): Promise<void> {
      const id = (userID || "").trim();
      if (!id) throw new Error("userID 为空");
      const { errCode, errMsg } = await this.requireSdk().deleteFriend(id);
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }

    async addBlack(userID: string, ex = ""): Promise<void> {
      const id = (userID || "").trim();
      if (!id) throw new Error("userID 为空");
      const { errCode, errMsg } = await this.requireSdk().addBlack({
        toUserID: id,
        ex,
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }

    async setFriendRemark(userID: string, remark: string): Promise<void> {
      const id = (userID || "").trim();
      if (!id) throw new Error("userID 为空");
      const { errCode, errMsg } = await this.requireSdk().updateFriends({
        friendUserIDs: [id],
        remark: remark.trim(),
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }

    async getJoinedGroups(): Promise<JoinedGroupBrief[]> {
      const { data, errCode, errMsg } =
        await this.requireSdk().getJoinedGroupList();
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
      return (data || []).map((g) => ({
        groupID: g.groupID,
        groupName: g.groupName || g.groupID,
        memberCount: g.memberCount || 0,
      }));
    }

    async inviteToGroup(params: {
      groupID: string;
      userIDList: string[];
      reason?: string;
    }): Promise<void> {
      const groupID = (params.groupID || "").trim();
      const userIDList = (params.userIDList || [])
        .map((x) => x.trim())
        .filter(Boolean);
      if (!groupID) throw new Error("groupID 为空");
      if (userIDList.length === 0) throw new Error("userIDList 为空");
      // reason 必填（SDK GroupMemberOperationParams）
      const { errCode, errMsg } = await this.requireSdk().inviteUserToGroup({
        groupID,
        userIDList,
        reason: params.reason || "邀请入群",
      });
      if (errCode !== 0) throw new Error(errMsg || String(errCode));
    }
  
    onConversationsChanged(cb: (list: ImConversation[]) => void) {
      this.convListeners.add(cb);
      return () => this.convListeners.delete(cb);
    }
  
    onNewMessages(cb: (msgs: ImMessage[]) => void) {
      this.msgListeners.add(cb);
      return () => this.msgListeners.delete(cb);
    }
  
    onState(cb: (s: ImConnectionState, err?: string) => void) {
      const wrap: Listener<{ s: ImConnectionState; err?: string }> = ({
        s,
        err,
      }) => cb(s, err);
      this.stateListeners.add(wrap);
      cb(this.state);
      return () => this.stateListeners.delete(wrap);
    }
  
    onFriendApplicationChanged(cb: () => void) {
      this.friendAppListeners.add(cb);
      return () => this.friendAppListeners.delete(cb);
    }
  }
  
  let _gateway: OpenImSdkGateway | null = null;
  export function getImGateway(): IImGateway {
    if (!_gateway) _gateway = new OpenImSdkGateway();
    return _gateway;
  }

